import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  MeetingSession,
  AudioVideoObserver,
  VideoTileState,
} from 'amazon-chime-sdk-js';
import type { VideoTileData } from '../types';

interface UseVideoTilesProps {
  meetingSession: MeetingSession | null;
}

interface UseVideoTilesReturn {
  localTileId: number | null;
  remoteTiles: VideoTileData[];
  bindVideoElement: (tileId: number, element: HTMLVideoElement | null) => void;
}

export function useVideoTiles({
  meetingSession,
}: UseVideoTilesProps): UseVideoTilesReturn {
  const [localTileId, setLocalTileId] = useState<number | null>(null);
  const [remoteTiles, setRemoteTiles] = useState<VideoTileData[]>([]);
  const boundElements = useRef<Map<number, HTMLVideoElement>>(new Map());

  const bindVideoElement = useCallback(
    (tileId: number, element: HTMLVideoElement | null) => {
      if (!meetingSession) return;

      if (element) {
        meetingSession.audioVideo.bindVideoElement(tileId, element);
        boundElements.current.set(tileId, element);
      } else {
        meetingSession.audioVideo.unbindVideoElement(tileId);
        boundElements.current.delete(tileId);
      }
    },
    [meetingSession]
  );

  useEffect(() => {
    if (!meetingSession) {
      // Reset tile state when session ends to prevent stale IDs from flashing
      // when user rejoins (e.g., "Try Again" after a failed connection)
      setLocalTileId(null);
      setRemoteTiles([]);
      return;
    }

    const observer: AudioVideoObserver = {
      videoTileDidUpdate: (tileState: VideoTileState) => {
        if (!tileState.tileId) return;

        const tile: VideoTileData = {
          tileId: tileState.tileId,
          attendeeId: tileState.boundAttendeeId || '',
          localTile: tileState.localTile,
        };

        if (tileState.localTile) {
          setLocalTileId(tileState.tileId);
        } else {
          setRemoteTiles((prev) => {
            const existing = prev.findIndex(
              (t) => t.tileId === tileState.tileId
            );
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = tile;
              return updated;
            }
            return [...prev, tile];
          });
        }
      },

      videoTileWasRemoved: (tileId: number) => {
        if (boundElements.current.has(tileId)) {
          meetingSession.audioVideo.unbindVideoElement(tileId);
          boundElements.current.delete(tileId);
        }

        setRemoteTiles((prev) => prev.filter((t) => t.tileId !== tileId));
        setLocalTileId((prev) => (prev === tileId ? null : prev));
      },
    };

    meetingSession.audioVideo.addObserver(observer);

    // Capture audioVideo reference for cleanup - meetingSession is from closure
    // but we need to unbind all tiles that exist at cleanup time
    const audioVideo = meetingSession.audioVideo;

    return () => {
      audioVideo.removeObserver(observer);
      // Use boundElements.current directly to capture all tiles bound during the session,
      // not just those bound at effect setup time
      boundElements.current.forEach((_, tileId) => {
        audioVideo.unbindVideoElement(tileId);
      });
      boundElements.current.clear();
    };
  }, [meetingSession]);

  return {
    localTileId,
    remoteTiles,
    bindVideoElement,
  };
}
